"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UserInteraction = void 0;
const mobx_1 = require("mobx");
const log_client_1 = require("@video/log-client");
const event_emitter_1 = require("../events/event-emitter");
const adapter_1 = require("../../../api/adapter");
const user_activation_1 = require("../../../api/adapter/features/user-activation");
const __1 = require("..");
class UserInteraction extends event_emitter_1.ObservableEventEmitter {
    constructor(options) {
        super();
        this.hadInteraction = false;
        this.audioCtx = null;
        this.audioEl = null;
        this.audioTrack = null;
        this.logError = (msg) => {
            var _a, _b;
            (_b = (_a = this.logger).info) === null || _b === void 0 ? void 0 : _b.call(_a, `silentAudio: ${msg}`);
        };
        (0, mobx_1.makeObservable)(this, {
            hadInteraction: mobx_1.observable,
        });
        this.logger = new log_client_1.LoggerCore("VDC-core").setLoggerMeta("client", "VDC").appendChain(UserInteraction);
        this.logger.info("constructor()", options);
        this.options = options;
        if (this.options.silentAudio.playSilentAudio) {
            this.once("hadInteraction", this.playSilentAudio);
        }
        this.init();
        this.addInnerDisposer(() => {
            this.stopSilentAudio();
            user_activation_1.userActivationEvents.forEach((e) => {
                if (adapter_1.device.isImplements(adapter_1.Feature.DOCUMENT))
                    adapter_1.device.document.body.removeEventListener(e, this.handleUserInteractionDOMEvent);
            });
        });
    }
    async init() {
        this.logger.info("init()");
        if (!adapter_1.device.isImplements(adapter_1.Feature.DOCUMENT)) {
            this.logger.warn("device does not support UserActivationFeature");
            return;
        }
        // If the browser supports UserActivation and user has already interacted with the page
        if (adapter_1.device.isImplements(adapter_1.Feature.USER_ACTIVATION) && adapter_1.device.userActivation.hasBeenActive) {
            this.hadInteraction = adapter_1.device.userActivation.hasBeenActive;
            this.logger.info(`hasBeenActive: ${adapter_1.device.userActivation.hasBeenActive}`);
            return;
        }
        // If browser doesn't support UserActivation or user has not been active,
        // try to start silent audio in case user has already interacted with
        // the page before this function was called.
        try {
            this.logger.info("trying to play audio to determine if user has interacted with page");
            // start audio (looping or not)
            await this.playSilentAudio();
            this.hadInteraction = true;
        }
        catch (err) {
            this.stopSilentAudio();
            this.logger.info("unable to play audio, attaching userActivation event listeners");
            // (1) attach eventListeners for userActivation events.
            user_activation_1.userActivationEvents.forEach((e) => {
                if (adapter_1.device.isImplements(adapter_1.Feature.DOCUMENT))
                    adapter_1.device.document.body.addEventListener(e, this.handleUserInteractionDOMEvent);
            });
            // (2) remove eventListeners
            this.once("hadInteraction", () => {
                user_activation_1.userActivationEvents.forEach((e) => {
                    if (adapter_1.device.isImplements(adapter_1.Feature.DOCUMENT))
                        adapter_1.device.document.body.removeEventListener(e, this.handleUserInteractionDOMEvent);
                });
            });
        }
    }
    async playSilentAudio() {
        var _a, _b;
        this.logger.info("playSilentAudio()", this.options);
        if (!adapter_1.device.isImplements(adapter_1.Feature.AUDIO_CONTEXT) ||
            !adapter_1.device.isImplements(adapter_1.Feature.MEDIA_STREAM) ||
            !adapter_1.device.isImplements(adapter_1.Feature.CREATE_AUDIO_ELEMENT) ||
            !adapter_1.device.isImplements(adapter_1.Feature.DOCUMENT)) {
            return;
        }
        this.audioCtx = new adapter_1.device.AudioContext();
        this.audioTrack = (0, __1.createSilentAudioTrack)(this.audioCtx);
        // check to ensure we have an audio track and the mediaStreamController has set it's videoTracks.\
        // create our new MediaStream and set our newly created audioTrack and the mediaStreamController video track to it.
        const newStream = new adapter_1.device.MediaStream([this.audioTrack]);
        const el = adapter_1.device.createAudioElement();
        el.srcObject = newStream;
        this.audioEl = adapter_1.device.document.body.appendChild(el);
        (_a = this.audioEl) === null || _a === void 0 ? void 0 : _a.setAttribute("data-silent", "true");
        try {
            await ((_b = this.audioEl) === null || _b === void 0 ? void 0 : _b.play());
            if (this.options.silentAudio.infiniteLoop === false || this.options.silentAudio.playSilentAudio === false) {
                setTimeout(() => {
                    this.stopSilentAudio();
                }, 100);
            }
        }
        catch (err) {
            this.logError(err);
            throw err;
        }
    }
    stopSilentAudio() {
        var _a, _b, _c;
        this.logger.info("stopSilentAudio()", this.options);
        (_a = this.audioTrack) === null || _a === void 0 ? void 0 : _a.stop();
        (_b = this.audioCtx) === null || _b === void 0 ? void 0 : _b.close().catch(this.logError);
        (_c = this.audioEl) === null || _c === void 0 ? void 0 : _c.remove();
        this.audioCtx = null;
        this.audioEl = null;
        this.audioTrack = null;
    }
    /**
     *  Determines if the Keyboard or Pointer event qualifies as a valid userInteraction event.
     * @param ev
     * @returns boolean
     */
    isValidInteractionEvent(ev) {
        // Invalid events for sticky user interaction
        // "keydown", provided the key is neither the Esc key nor a shortcut key reserved by the user agent.
        if ((0, user_activation_1.isKeyboardEvent)(ev) && ev.type === "keydown" && ev.keyCode === 27) {
            return false;
        }
        // "pointerdown", provided the event's pointerType is "mouse".
        if ((0, user_activation_1.isPointerEvent)(ev) && ev.type === "pointerup" && ev.pointerType === "mouse") {
            return false;
        }
        // "pointerup", provided the event's pointerType is not "mouse".
        if ((0, user_activation_1.isPointerEvent)(ev) && ev.type === "pointerdown" && ev.pointerType !== "mouse") {
            return false;
        }
        // "touchend
        if (ev.type === "touchend") {
            return false;
        }
        return true;
    }
    /**
     * If user had a valid interaction, sets hadInteraction to true and emits event.
     * @param ev
     * @returns void
     *
     */
    handleUserInteractionDOMEvent(ev) {
        if (!this.isValidInteractionEvent(ev) || this.hadInteraction) {
            return;
        }
        this.hadInteraction = true;
        this.logger.info(`user interacted with page via ${ev.type}`);
    }
}
exports.UserInteraction = UserInteraction;
UserInteraction.displayName = "UserInteraction";
//# sourceMappingURL=index.js.map